<script lang="ts" setup>
import type {
	Cluster,
	onClusterClickHandler
} from '@googlemaps/markerclusterer';
import type { Ref } from 'vue';

import {
	bindEvents,
	bindProps,
	getComponentEventsConfig,
	getComponentPropsConfig,
	getPropsValuesWithoutOptionsProp
} from '@/core/service/googleMaps/utils';
import {
	clusterKey,
	mapPromiseKey as mapPromiseKeySymbol
} from '@/core/service/googleMaps/utils/symbols';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { useGoogleMaps } from '~/composables/useGoogleMaps';
import {
	inject,
	onBeforeMount,
	onBeforeUnmount,
	onUnmounted,
	onUpdated,
	provide,
	ref
} from 'vue';

defineOptions({ name: 'GmapCluster' });

const props = withDefaults(defineProps<ClusterProps>(), {});

const emits = defineEmits<{
	clusteringBegin: [];
	clusteringEnd: [];
	click: [value: google.maps.MapMouseEvent];
	rightClick: [value: google.maps.MapMouseEvent];
	dblClick: [value: google.maps.MapMouseEvent];
	drag: [value: google.maps.MapMouseEvent];
	dragEnd: [value: google.maps.MapMouseEvent];
	dragStart: [value: google.maps.MapMouseEvent];
	mouseDown: [value: google.maps.MapMouseEvent];
	mouseOut: [value: google.maps.MapMouseEvent];
	mouseOver: [value: google.maps.MapMouseEvent];
	mouseUp: [value: google.maps.MapMouseEvent];
}>();

/**
 * Cluster component
 * @displayName GmvCluster
 * @see [source code](/guide/cluster.html#source-code)
 * @see [Official documentation](https://googlemaps.github.io/js-markerclusterer/modules.html)
 * @see [Marker clusterer](https://developers.google.com/maps/documentation/javascript/marker-clustering#maps_marker_clustering-javascript)
 */

interface ClusterProps {
	algorithm?: Algorithm;
	markers?: google.maps.marker.AdvancedMarkerElement[];
	onClusterClick?: onClusterClickHandler;
	mapPromiseKey?: string;
	options?: Record<string, any>;
}

const { api: googleMapsApi } = useGoogleMaps();
type MapInstanceRef = Ref<google.maps.Map | undefined>;
type ClusterInstanceRef = Ref<MarkerClusterer | undefined>;

const mapPromise = inject<Promise<google.maps.Map | null> | null>(
	mapPromiseKeySymbol,
	null
);
const mapInstance: MapInstanceRef = ref();
const clusterInstance: ClusterInstanceRef = ref();

function getRenderer() {
	return {
		render({ count, position }: Cluster) {
			if (!googleMapsApi.value) {
				return null;
			}

			const clusterEl = document.createElement('div');
			const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="37" height="37" viewBox="0 0 37 37" fill="none">
                        <g filter="url(#filter0_d_1142_41294)">
                            <circle cx="18.9688" cy="17.5" r="14" fill="white"/>
                        </g>
                        <defs>
                            <filter id="filter0_d_1142_41294" x="0.96875" y="0.5" width="36" height="36" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                            <feFlood flood-opacity="0" result="BackgroundImageFix"/>
                            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                            <feMorphology radius="1" operator="dilate" in="SourceAlpha" result="effect1_dropShadow_1142_41294"/>
                            <feOffset dy="1"/>
                            <feGaussianBlur stdDeviation="1.5"/>
                            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.15 0"/>
                            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_1142_41294"/>
                            <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_1142_41294" result="shape"/>
                            </filter>
                        </defs>
                    </svg>`;
			clusterEl.style.backgroundImage = `url('data:image/svg+xml;utf8,${encodeURIComponent(svg)}')`;
			clusterEl.classList.add('cluster');
			clusterEl.textContent = count.toString();

			return new googleMapsApi.value.marker.AdvancedMarkerElement({
				position,
				content: clusterEl,
				zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count
			});
		}
	};
}

async function initCluster() {
	const initialOptions: ClusterProps & {
		[key: string]: any;
	} = {
		...getPropsValuesWithoutOptionsProp(props),
		...props.options
	};

	const { markers, onClusterClick } = initialOptions;

	clusterInstance.value = new MarkerClusterer({
		map: toRaw(mapInstance.value),
		renderer: getRenderer(),
		markers,
		onClusterClick
	});
}

function bindComponent() {
	const clusterIconPropsConfig = getComponentPropsConfig('GmpCluster');
	const clusterIconEventsConfig = getComponentEventsConfig(
		'GmpCluster',
		'auto'
	);

	bindProps(clusterIconPropsConfig, clusterInstance, emits as any, props);

	bindEvents(clusterIconEventsConfig, clusterInstance, emits as any);
}

onBeforeMount(async () => {
	const map = mapPromise ? await mapPromise : null;
	mapInstance.value = map || undefined;

	await initCluster();
	bindComponent();
});

onBeforeUnmount(async () => {
	if (clusterInstance.value) {
		clusterInstance.value.clearMarkers();
	}
});

onUnmounted(async () => {
	if (clusterInstance.value) {
		clusterInstance.value.setMap(null);
	}
});

onUpdated(async () => {
	if (clusterInstance.value) {
		clusterInstance.value.render();
	}
});

provide(clusterKey, clusterInstance);

defineExpose({ mapInstance, clusterInstance });
</script>

<template>
	<div>
		<!-- @slot Used to set your cluster -->
		<slot></slot>
	</div>
</template>
